import '../../components/ui/dialog-layout.css';
/* Salesforce Campaign ID Field Mapper Modal */
import { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogBody, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ArrowsRightLeftIcon, ArrowRightIcon } from "@heroicons/react/24/solid";

interface SalesforceFieldMapperModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  formName: string;
  campaignId: string;
  setCampaignId: (id: string) => void;
}

export default function SalesforceFieldMapperModal({
  open,
  setOpen,
  formName,
  campaignId,
  setCampaignId,
}: SalesforceFieldMapperModalProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [localCampaignId, setLocalCampaignId] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (open) {
      setLocalCampaignId(campaignId || "");
      setValidationError("");
    }
  }, [open, campaignId]);

  const validateCampaignId = (): boolean => {
    if (!localCampaignId || localCampaignId.trim() === "") {
      setValidationError("Campaign ID is required");
      return false;
    }

    if (localCampaignId.trim().length !== 18) {
      setValidationError("Campaign ID must be exactly 18 characters");
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (validateCampaignId()) {
      setCampaignId(localCampaignId.trim());
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        showX={false}
        className="integration-mapping-dialog flex flex-col max-w-[500px] max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] overflow-hidden px-[18px] py-[22px]"
      >
        <DialogBody className="space-y-4">
          <div className="gap-[19px] sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-edge bg-paper-inset sm:mx-0 sm:h-10 sm:w-10">
              <ArrowsRightLeftIcon className="size-6 text-ink-muted" />
            </div>

            <div className="mt-0 flex-grow">
              <DialogTitle className="text-lg text-ink-muted">Map Fields</DialogTitle>
              <div className="mt-2">
                <p className="text-sm text-ink-muted">
                  Map your CharityStack fields to match the data structure of your
                  connected platform.
                </p>

                <div className="mt-4 flex flex-col gap-2">
                  <div className="mapping-column-headings flex items-center gap-2 self-stretch">
                    <div className="w-[170.5px] pl-[10px] text-base font-medium text-ink-muted">
                      CharityStack Values
                    </div>
                    <div className="w-[18px]"></div>
                    <div className="w-[170.5px] pl-[10px] text-base font-medium text-ink-muted">
                      Salesforce Values
                    </div>
                  </div>

                  <div className="mapping-field-row flex items-center justify-center gap-2 self-stretch">
                    <span className="text-xs text-ink-muted sm:hidden">CharityStack Values</span>
                    {/* Left side - Form Name (read-only) */}
                    <div className="my-auto flex h-[32px] w-[170.5px] shrink-0 items-center self-stretch rounded-md border border-edge bg-paper-inset px-[6px] py-0">
                      <span
                        className="truncate text-base text-ink-secondary"
                        title={formName}
                      >
                        {formName}
                      </span>
                    </div>

                    {/* Arrow */}
                    <div className="mapping-direction flex aspect-square h-[18px] w-[18px] text-ink-secondary">
                      <ArrowRightIcon />
                    </div>

                    {/* Right side - Campaign ID label and input */}
                    <div className="flex w-[170.5px] flex-col items-start gap-1 self-stretch">
                      <span className="text-xs text-ink-muted sm:hidden">Salesforce Values</span>
                      {/* Campaign ID label (styled as input) */}
                      <div className="flex h-[32px] w-full items-center justify-between self-stretch rounded-md border border-edge bg-paper-muted px-[6px] py-0">
                        <span className="text-base text-black">Campaign ID</span>
                      </div>

                      {/* Campaign ID text input */}
                      <input
                        type="text"
                        className={`flex h-[32px] w-full items-center justify-between self-stretch rounded-md border ${
                          validationError ? "border-red-500" : "border-edge"
                        } bg-paper-muted px-[6px] py-0 text-base text-black focus:border-edge focus:outline-none focus:ring-0`}
                        placeholder="Enter 18-character ID"
                        value={localCampaignId}
                        onChange={(e) => {
                          setLocalCampaignId(e.target.value);
                          setValidationError("");
                        }}
                        maxLength={18}
                        aria-label="Salesforce Campaign ID"
                      />
                    </div>
                  </div>

                  {validationError && (
                    <p className="text-sm text-red-500">{validationError}</p>
                  )}
                </div>

                <Separator className="mb-0 mt-4 bg-paper-inset text-edge" />
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="neutral"
            size="workspace"
            onClick={() => setOpen(false)}
            ref={cancelButtonRef}
          >
            Close
          </Button>

          <Button
            type="button"
            variant="confirm"
            size="workspace"
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
